import { CalendarBlankIcon } from "@phosphor-icons/react/dist/ssr/CalendarBlank";
import { useEffect, useRef, useState } from "react";

import { analyticsEvent } from "@up/analytics";
import {
  getInventoryItemsGroupedByCatalogueItems,
  logError,
  useTheme,
} from "@up/data";
import { Spinner } from "@up/ui";

import { useCart } from "../CartContext";

export default function ChangeDate(props: {
  cancel: () => void;
  currentCohortId: string;
  catalogueItemId: string;
}) {
  const cart = useCart();
  const theme = useTheme();
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [didError, setDidError] = useState(false);
  const [potentialCohorts, setPotentialCohorts] = useState<
    Array<{
      _id: string;
      name: string;
    }>
  >([]);

  const heading = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (heading.current) {
      heading.current.focus();
    }
  }, []);

  useEffect(() => {
    const go = async function () {
      setIsLoadingDates(true);

      try {
        const _catalogueItems = await getInventoryItemsGroupedByCatalogueItems(
          props.catalogueItemId,
        );

        setPotentialCohorts(
          _catalogueItems[0].cohorts.filter((cohort) => {
            if (cohort._id === props.currentCohortId) {
              return false;
            }

            const match = cart.data?.lineItems?.find((lineItem) => {
              return lineItem._id === cohort._id;
            });

            if (match) {
              return false;
            }

            return true;
          }),
        );

        setIsLoadingDates(false);
      } catch (err) {
        setIsLoadingDates(false);
        setDidError(true);
        logError({
          action: "CHECKOUT_CHANGE_DATE_LOAD",
          error: err,
          context: { catalogueItemId: props.catalogueItemId },
        });
      }
    };

    go();
  }, [cart.data?.lineItems, props.currentCohortId, props.catalogueItemId]);

  return (
    <div
      className="animation-fade-in relative col-span-3 mt-2 flex flex-grow flex-col items-center px-3 py-5 text-center bg-blue-50"
      data-testid="checkout-list-item-change-date"
      data-location="checkout"
    >
      <CalendarBlankIcon size={56} color="#00d" />
      <div className="mb-2 pt-1 text-2xl" ref={heading} tabIndex={0}>
        Change Dates
      </div>

      <form
        className="max-w-full"
        onSubmit={async (e) => {
          e.preventDefault();
          setIsSaving(true);
          const data = new FormData(e.target as HTMLFormElement);
          const newID = data.get("newCohortId") as string | null;

          if (!newID) {
            return;
          }

          try {
            await cart.addItem(newID);
            await cart.removeItem(props.currentCohortId);
            analyticsEvent({
              event: "cartChangeDates",
              location: "checkout",
            });
          } catch (err) {
            logError({ action: "CART_CHANGE_DATE", error: err });
          } finally {
            setIsSaving(false);
          }
        }}
      >
        {isSaving ? (
          <div className="flex items-center justify-center gap-2 w-full">
            Saving changes...
          </div>
        ) : (
          <div>
            <div className="mb-8">
              {isLoadingDates ? (
                <div className="flex items-center justify-center gap-2">
                  <Spinner />
                  Loading cohorts
                </div>
              ) : potentialCohorts.length ? (
                <select
                  name="newCohortId"
                  className="bg-white p-1 rounded border border-blue-100 text-ellipsis max-w-full text-sm"
                >
                  {potentialCohorts.map((c) => {
                    return (
                      <option value={c._id} key={c._id}>
                        {c.name}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <div className="text-base">
                  No additional cohorts are available
                </div>
              )}
            </div>
            <div className="flex items-center justify-center">
              <button
                className={`mr-2 rounded border border-solid border-blue-700 bg-blue-600 px-4 py-1 text-white text-base ${
                  potentialCohorts.length ? "" : "cursor-not-allowed opacity-60"
                }`}
                data-testid="checkout-list-item-change-dates-yes"
                data-location="checkout"
                type="submit"
                disabled={!potentialCohorts.length}
              >
                Change dates
              </button>
              <button
                type="button"
                className="rounded border border-solid border-white bg-white px-4 py-1 text-gray-700 text-base"
                data-testid="checkout-list-item-change-dates-no"
                data-location="checkout"
                onClick={props.cancel}
              >
                cancel
              </button>
            </div>
          </div>
        )}
        {didError ? (
          <div className="pt-2">
            Sorry, an error has occurred. Please try again. If you keep seeing
            this error,{" "}
            <a className="underline" href={`mailto:${theme.supportEmail}`}>
              email support
            </a>
          </div>
        ) : null}
      </form>
    </div>
  );
}
